import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useTenantSelection } from '@/contexts/TenantSelectionContext';
import PlatformAdminNavigation from '@/components/PlatformAdminNavigation';
import { PlatformAdminNavigation as SidebarNavigation } from '@/components/platform-admin/PlatformAdminNavigation';
import { SidebarProvider } from '@/components/ui/sidebar';
import { TenantSelector } from '@/components/platform-admin/TenantSelector';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Bot, MessageSquare, BarChart3, ScrollText, Settings } from 'lucide-react';

interface TenantFeatureSelectorProps {
  feature: 'ai-agents' | 'whatsapp' | 'analytics' | 'logging' | 'settings';
}

// Titles/descriptions come from platformAdmin's per-feature sections (aiAgentManagement,
// whatsappManagement, analyticsDashboard, loggingDebug, tenantSettings) so this selector
// stays in sync with the same copy already used on each feature's own screen.
const featureConfig = {
  'ai-agents': {
    i18nKey: 'aiAgentManagement',
    icon: Bot,
  },
  'whatsapp': {
    i18nKey: 'whatsappManagement',
    icon: MessageSquare,
  },
  'analytics': {
    i18nKey: 'analyticsDashboard',
    icon: BarChart3,
  },
  'logging': {
    i18nKey: 'loggingDebug',
    icon: ScrollText,
  },
  'settings': {
    i18nKey: 'tenantSettings',
    icon: Settings,
  },
};

export function TenantFeatureSelector({ feature }: TenantFeatureSelectorProps) {
  const { t } = useTranslation('platformAdmin');
  const { selectedTenant } = useTenantSelection();
  const navigate = useNavigate();
  const config = featureConfig[feature];
  const IconComponent = config.icon;
  const title = t(`${config.i18nKey}.title`);
  const description = t(`${config.i18nKey}.description`);

  useEffect(() => {
    if (selectedTenant) {
      navigate(`/platform-admin/tenant/${selectedTenant.id}/${feature}`);
    }
  }, [selectedTenant, feature, navigate]);

  return (
    <div className="min-h-screen bg-background">
      <PlatformAdminNavigation />
      <SidebarProvider>
        <div className="flex w-full">
          <SidebarNavigation />
          <main className="flex-1 min-w-0">
            <div className="container mx-auto px-4 py-8">
              <div className="max-w-2xl mx-auto">
                <Card>
                  <CardHeader className="text-center">
                    <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle>{title}</CardTitle>
                    <CardDescription>
                      {t('featureSelector.selectTenantToContinue', { description })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        {t('selectTenant')}
                      </label>
                      <TenantSelector />
                    </div>
                    <div className="text-center text-sm text-muted-foreground">
                      {t('featureSelector.redirectNotice', { feature: title })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </main>
        </div>
      </SidebarProvider>
    </div>
  );
}